/**
 * EL PENSUM DE LOS AGENTES EN CASCADA (PENSUM-PANEL-CASCADA-T1; Josse 21-oct, SI de Ares #32039).
 *
 * Josse pidio poco texto inicial y busqueda en cascada: agente -> skill -> unidad. La forma es la de los planes de Atenea
 * (PROP-PENSUM-P-001 / D-001, #31563) servida por GET learning/pensum/agentes (contrato propuesto por P en el [COORD] de
 * la tarea; A lo confirma). El panel LEE estado y avance: no decide si una unidad esta aprobada.
 *
 * LO QUE NO SE INVENTA: un estado que no se reconoce no se redondea a "no empezada"; se dice que no se pudo leer. El
 * avance es n de N unidades aprobadas, sin porcentaje (Atenea: porcentaje no disponible hasta aprobar version y
 * vinculos). Un enlace de evidencia solo es enlace si es http(s); si no, se muestra la referencia como texto.
 *
 * Sin DOM: lo prueba scripts/check-pensum-cascada.mjs.
 *
 * @module application/pensum/pensumCascada
 */

const de = (o, ...ks) => { for (const k of ks) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const texto = (v) => (v === undefined || v === null ? '' : String(v).trim());
const lista = (v) => (Array.isArray(v) ? v : []);

export const ESTADOS = {
  no_evaluada: 'No empezada',
  en_practica: 'En practica',
  aprobada: 'Aprobada'
};

/** El texto del estado; uno desconocido no se disfraza de "no empezada". */
export function textoDeEstado(estado) {
  return ESTADOS[estado] || 'Estado sin leer';
}

const esEnlace = (u) => /^https?:\/\//i.test(texto(u));

function normalizarUnidad(u) {
  const id = texto(de(u, 'id', 'Id'));
  const dominio = texto(de(u, 'dominio', 'Dominio')).toLowerCase() || (/^BASE-/i.test(id) ? 'base' : 'especialidad');
  const intentos = lista(de(u, 'intentos', 'attempts')).map((i) => ({
    fecha: texto(de(i, 'fecha', 'at', 'date')).slice(0, 10),
    revisor: texto(de(i, 'revisor', 'reviewer')),
    resultado: texto(de(i, 'resultado', 'result')),
    evidencia: lista(de(i, 'evidencia', 'evidence')).map((e) => {
      const url = texto(de(e, 'url', 'Url'));
      return { tipo: texto(de(e, 'tipo', 'type')), ref: texto(de(e, 'ref', 'Ref')), url: esEnlace(url) ? url : '' };
    })
  }));
  return {
    id,
    titulo: texto(de(u, 'titulo', 'title')),
    skill: texto(de(u, 'skill', 'Skill')),
    skillVersion: texto(de(u, 'skillVersion', 'skill_version')),
    dominio,
    prerrequisitos: lista(de(u, 'prerrequisitos', 'prerequisites')).map(texto),
    criterio: texto(de(u, 'criterio', 'criterion')),
    evidenciaRequerida: texto(de(u, 'evidenciaRequerida', 'evidencia_requerida', 'evidence_required')),
    estado: texto(de(u, 'estado', 'state')).toLowerCase(),
    intentos
  };
}

/**
 * La respuesta en una forma: un agente con su avance y sus skills, cada skill con sus unidades en el orden en que
 * llegaron. Ordenados por codigo de agente.
 */
export function normalizarPensumAgentes(data) {
  const d = data || {};
  const items = Array.isArray(d) ? d : lista(de(d, 'items', 'Items'));
  return items.map((a) => {
    const plan = de(a, 'plan', 'Plan') || {};
    const unidades = lista(de(a, 'unidades', 'units')).map(normalizarUnidad);
    const skills = [];
    for (const u of unidades) {
      let s = skills.find((x) => x.skill === u.skill);
      if (!s) { s = { skill: u.skill, unidades: [] }; skills.push(s); }
      s.unidades.push(u);
    }
    // El avance de la skill se fija aqui, sobre TODAS sus unidades: la busqueda recorta la lista, no el conteo.
    for (const s of skills) { s.total = s.unidades.length; s.aprobadas = s.unidades.filter((u) => u.estado === 'aprobada').length; }
    const totalServidor = de(a, 'unidadesTotal', 'unidades_total', 'required_units');
    return {
      agente: texto(de(a, 'agente', 'agent', 'subjectId')),
      plan: { id: texto(de(plan, 'id', 'Id')), version: texto(de(plan, 'version', 'Version')), estado: texto(de(plan, 'estado', 'status')) },
      total: Number.isFinite(Number(totalServidor)) && totalServidor !== '' ? Number(totalServidor) : unidades.length,
      aprobadas: unidades.filter((u) => u.estado === 'aprobada').length,
      siguiente: texto(de(a, 'siguiente', 'next')),
      skills
    };
  }).sort((x, y) => x.agente.localeCompare(y.agente));
}

const contiene = (q, ...vals) => vals.some((v) => texto(v).toLowerCase().includes(q));

/**
 * La busqueda en cascada: deja los agentes, skills y unidades que calzan con q. Si calza el agente, queda entero; si
 * calza la skill, queda con todas sus unidades; si no, solo las unidades que calzan. Vacio = todo.
 */
export function filtrarPensum(agentes, q) {
  const b = texto(q).toLowerCase();
  if (!b) return agentes;
  const out = [];
  for (const a of agentes) {
    if (contiene(b, a.agente, a.plan.id)) { out.push(a); continue; }
    const skills = [];
    for (const s of a.skills) {
      if (contiene(b, s.skill)) { skills.push(s); continue; }
      const unidades = s.unidades.filter((u) => contiene(b, u.id, u.titulo, u.criterio, u.evidenciaRequerida));
      if (unidades.length) skills.push({ ...s, unidades });
    }
    if (skills.length) out.push({ ...a, skills });
  }
  return out;
}
